import { useEffect, useRef, useState } from "react"
import { Routes, Route, Navigate, useNavigate } from "react-router-dom"
import {
  createAppSession,
  createBridge,
  type AppBridge,
  type AppClient,
  type AppContext,
} from "@koortex/apps-sdk"
import { Home } from "./pages/Home"
import { OrderDetail } from "./pages/OrderDetail"
import { Settings } from "./pages/Settings"
import { Logistica } from "./pages/Logistica"
import { LogisticaOrder } from "./pages/LogisticaOrder"
import { Tracking } from "./pages/Tracking"

/** Roles ≥ this see the full fulfillment app; below it = logistics-only.
 *  Koortex roles: 10 ADMIN · 5 MANAGER · 3 USER · 1 PENDING. */
const FULL_APP_MIN_ROLE = 5

export interface AppShell {
  context: AppContext
  client: AppClient
  bridge: AppBridge
  /**
   * Numeric role from the host token. 10 = ADMIN, 5 = MANAGER, 3 = USER.
   * Not part of `AppContext` because the SDK strips unknown claims; decoded
   * inline from the JWT body. Used to gate admin-only UI like /settings.
   */
  role: number
}

function decodeRoleFromToken(token: string): number {
  try {
    const body = token.split(".")[1]
    if (!body) return 3
    const normalized = body.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    )
    const json = typeof atob === "function" ? atob(padded) : ""
    const parsed = JSON.parse(json) as { role?: unknown }
    const raw = parsed.role
    return typeof raw === "number" && Number.isInteger(raw) ? raw : 3
  } catch {
    return 3
  }
}

/**
 * The host (`koortex-app`) opens this app inside an iframe with a shell token
 * appended to the URL as `?token=...`.
 *
 * The token lasts 30 min and travels in the iframe URL, so the app must NEVER
 * reload to get a fresh one — the iframe would reopen with the same expired
 * token and the operator would lose what's on screen. `createAppSession`
 * (@koortex/apps-sdk ≥ 0.5.0) handles the lifecycle:
 *   - the host pushes a renewed token 5 min before `exp` and on tab focus
 *     (`{ type: "koortex:token" }`); the session swaps it in place;
 *   - `client` is STABLE for the whole session and always sends the newest
 *     token — it never changes identity, so nothing re-renders;
 *   - a request that still hits 401 "token expired" asks the host for a token
 *     and retries once.
 * Before this, the client held the token as a fixed string: after 30 min the
 * 5 s/8 s polling of the order and logistics screens started failing with 401.
 *
 * Exposes:
 *   - `context` → claims (tenantId, userId, locale, permissions); stable across
 *                 refreshes (same user + installation)
 *   - `client`  → authenticated HTTP client against /api/apps/<key>/...
 *   - `bridge`  → typed postMessage bridge (toast, navigate.crm, etc.)
 */
function useAppShell(): AppShell | null {
  const [shell, setShell] = useState<AppShell | null>(null)

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token")
    if (!token) {
      console.warn("[app] No token in URL — running standalone (dev mode).")
      return
    }

    let session: ReturnType<typeof createAppSession>
    try {
      session = createAppSession(token) // throws on an unreadable token
    } catch (err) {
      console.error("[app] Invalid token:", err)
      return
    }

    const bridge = createBridge({
      // Only capabilities declared in manifest.json will succeed at runtime.
      allowedCapabilities: ["toast", "navigate.crm", "request.action", "refresh.badge"],
    })

    setShell({
      context: session.context,
      client: session.client,
      bridge,
      // The role doesn't change on refresh (same user), so the initial token is enough.
      role: decodeRoleFromToken(token),
    })

    return () => session.dispose()
  }, [])

  return shell
}

/**
 * O host abre sempre o entrypoint da app (index.html?token=…): o caminho
 * pedido (ex.: /apps/shopify-fulfillment/logistica/order/<id>, o link "Abrir
 * no Koortex" do email do armazém) viaja no token, em `context.path`. Sem
 * isto a app abria na home e o link não levava a lado nenhum.
 */
function usePathFromToken(shell: AppShell | null): void {
  const navigate = useNavigate()
  const done = useRef(false)
  useEffect(() => {
    if (!shell || done.current) return
    done.current = true
    const path = (shell.context.path ?? "").trim()
    if (!path || path === "/") return
    const target = path.startsWith("/") ? path : `/${path}`
    if (target !== window.location.pathname) navigate(target, { replace: true })
  }, [shell, navigate])
}

export function App() {
  const shell = useAppShell()
  usePathFromToken(shell)

  // Logistics-only users (role < 5) get ONLY the logistics surface. Everything
  // else redirects to /logistica so they can't reach the full fulfillment app.
  // Note: this is a UI-level gate (the records API is shared); the actions
  // available to them are logistics-only anyway.
  const logisticsOnly = shell != null && shell.role < FULL_APP_MIN_ROLE

  if (logisticsOnly) {
    return (
      <div className="h-screen w-full bg-background text-foreground">
        <Routes>
          <Route path="/logistica" element={<Logistica shell={shell} />} />
          <Route path="/logistica/order/:id" element={<LogisticaOrder shell={shell} />} />
          <Route path="*" element={<Navigate to="/logistica" replace />} />
        </Routes>
      </div>
    )
  }

  return (
    <div className="h-screen w-full bg-background text-foreground">
      <Routes>
        <Route path="/" element={<Home shell={shell} />} />
        <Route path="/index.html" element={<Home shell={shell} />} />
        <Route path="/order/:id" element={<OrderDetail shell={shell} />} />
        <Route path="/logistica" element={<Logistica shell={shell} />} />
        <Route path="/logistica/order/:id" element={<LogisticaOrder shell={shell} />} />
        <Route path="/tracking" element={<Tracking shell={shell} />} />
        <Route path="/settings" element={<Settings shell={shell} />} />
        <Route path="*" element={<Home shell={shell} />} />
      </Routes>
    </div>
  )
}
